/* eslint-disable react/no-array-index-key */
import { Col, Row } from 'antd';
import classNames from 'classnames';
import DefaultDrawer from 'components/DefaultDrawer';
import SlideIn from 'components/SlideIn';
import useWindowSize from 'hooks/useWindowSize';
import { isFunction } from 'lodash';
import React, { useEffect, useState } from 'react';

import { IWithSlideInComponentProps } from './types';

/**
 * For creating component with extra slide-in component when clicking on one item
 * @param {IWithSlideInComponentProps} props
 * @param {Record<string, any>} props.mainContentDataWhenSlideIn - object that contains array of data to render when slide-in mode is activated
 * @param {GeneralFunction} props.mainContentDataConverter - function that get data from @param props.mainContentDataWhenSlideIn to render a new array of components
 * @param {GeneralFunction} props.displayDataKeysWhenSlideIn - keys that will be used for main content when slide-in mode is activated. if the key is "empty", @param props.mainContentDataConverter will skip this key.
 */
const WithSlideInComponent: React.FC<IWithSlideInComponentProps> = ({
  children,
  slideInProps,
  slideInVisible,
  mainContentDataWhenSlideIn,
  displayDataKeysWhenSlideIn,
  mainContentDataConverter,
  onContentClick,
  highlightActiveContent,
  activeKey,
  renderExtraWhenSlideIn,
  slideInStyle,
  mobileThreshold,
  className,
}) => {
  const [activeContent, setActiveContent] = useState<number | string>();
  const { isMobile } = useWindowSize(mobileThreshold);
  const slideInClassNames = classNames('slide-in', {
    mobile: isMobile,
    visible: slideInVisible && !isMobile,
  });
  useEffect(() => {
    if (activeKey) {
      setActiveContent(activeKey);
    }
  }, [activeKey]);
  const generateSlideInMainDataComponent = (data: Record<string, any>, index: number) => {
    const dataConverted = mainContentDataConverter?.(data) || data;
    const key = data.key || data.id || index;
    return (
      <Row
        key={key}
        className={`slide-visible-content-wrapper gx-m-0 ${
          activeContent === key && highlightActiveContent ? 'active' : ''
        }`}
        style={{ cursor: onContentClick || activeKey ? 'pointer' : undefined }}
        onClick={() => {
          if (highlightActiveContent) {
            setActiveContent(key);
          }
          onContentClick?.(data);
        }}>
        {displayDataKeysWhenSlideIn?.map((dataKey: string, keyIndex: number) =>
          !dataConverted[dataKey] && dataKey !== 'empty' ? null : (
            <Col
              className={`slide-visible-content gx-p-0 ${keyIndex % 2 === 0 ? 'left-side' : 'right-side'}`}
              key={keyIndex}
              span={12}>
              {dataConverted[dataKey]}
            </Col>
          ),
        )}
      </Row>
    );
  };
  return (
    <div className={`with-slide-in-container ${className}`}>
      <div className='main-contents' style={{ display: slideInVisible && !isMobile ? 'none' : undefined }}>
        {children}
      </div>
      <div
        className='slide-visible-main-contents'
        style={{ display: !slideInVisible || isMobile ? 'none' : undefined }}>
        {mainContentDataWhenSlideIn?.map(generateSlideInMainDataComponent)}
        {renderExtraWhenSlideIn && (
          <div className='slide-visible-main-contents-extra'>
            {isFunction(renderExtraWhenSlideIn) ? renderExtraWhenSlideIn() : renderExtraWhenSlideIn}
          </div>
        )}
      </div>
      {slideInProps?.children && (
        <>
          <div style={slideInStyle} className={slideInClassNames}>
            <SlideIn {...slideInProps} />
          </div>
          <DefaultDrawer
            className='drawer-w-100'
            headerStyle={{ display: 'none' }}
            visible={isMobile && slideInVisible}
            bodyStyle={{ padding: 0, margin: 0 }}>
            <SlideIn {...slideInProps} />
          </DefaultDrawer>
        </>
      )}
    </div>
  );
};

export default React.memo(WithSlideInComponent);
